import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Inbox from "@/pages/Inbox";
import Tickets from "@/pages/Tickets";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Calculator from "@/pages/Calculator";
import PricingSettings from "@/pages/PricingSettings";
import PnL from "@/pages/PnL";
import IVR from "@/pages/IVR";
import Analytics from "@/pages/Analytics";
import Team from "@/pages/Team";
import Passengers from "@/pages/Passengers";
import Gallery from "@/pages/Gallery";
import Showcase from "@/pages/Showcase";
import Shop from "@/pages/Shop";
import ShopManager from "@/pages/ShopManager";
import AgentAccess from "@/pages/AgentAccess";
import Offices from "@/pages/Offices";
import Groups from "@/pages/Groups";
import Flyer from "@/pages/Flyer";
import Muallems from "@/pages/Muallems";
import Owners from "@/pages/Owners";
import OfficeStaff from "@/pages/OfficeStaff";
import MemberTree from "@/pages/MemberTree";
import Commission from "@/pages/Commission";
import Profile from "@/pages/Profile";
import Prospectus from "@/pages/Prospectus";
import StaffLogin from "@/pages/StaffLogin";
import PilgrimCare from "@/pages/PilgrimCare";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalHome from "@/pages/portal/PortalHome";
import JobsPanel from "@/pages/JobsPanel";
import Leaderboard from "@/pages/Leaderboard";
import Telecaller from "@/pages/Telecaller";
import CustomerLogin from "@/pages/CustomerLogin";
import Accounts from "@/pages/Accounts";
import JobsPortal from "@/pages/JobsPortal";
import AiStudio from "@/pages/AiStudio";
import WhatsAppGroups from "@/pages/WhatsAppGroups";
import MyDesigns from "@/pages/MyDesigns";
import Certificates from "@/pages/Certificates";
import { AiChatWidget } from "@/components/ai/AiChatWidget";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return (
    <WorkspaceProvider>
      <Layout>{children}</Layout>
    </WorkspaceProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/agent" element={<AgentAccess />} />
            <Route path="/portal" element={<PortalLogin />} />
            <Route path="/customer-login" element={<CustomerLogin />} />
            <Route path="/portal/home" element={<PortalHome />} />
            <Route path="/showcase" element={<Showcase />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop-manager" element={<Protected><ShopManager /></Protected>} />
            <Route path="/prospectus" element={<Prospectus />} />
            <Route path="/staff-login" element={<StaffLogin />} />
            <Route path="/jobs" element={<JobsPortal />} />
            <Route path="/jobs-panel" element={<Protected><JobsPanel /></Protected>} />
            <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
            <Route path="/telecaller" element={<Protected><Telecaller /></Protected>} />
            <Route path="/" element={<Showcase />} />
            <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
            <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
            <Route path="/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
            <Route path="/calculator" element={<Protected><Calculator /></Protected>} />
            <Route path="/pricing-settings" element={<Protected><PricingSettings /></Protected>} />
            <Route path="/accounts" element={<Protected><Accounts /></Protected>} />
            <Route path="/pnl" element={<Protected><PnL /></Protected>} />
            <Route path="/ivr" element={<Protected><IVR /></Protected>} />
            <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
            <Route path="/team" element={<Protected><Team /></Protected>} />
            <Route path="/passengers" element={<Protected><Passengers /></Protected>} />
            <Route path="/gallery" element={<Protected><Gallery /></Protected>} />
            <Route path="/offices" element={<Protected><Offices /></Protected>} />
            <Route path="/groups" element={<Protected><Groups /></Protected>} />
            <Route path="/flyer" element={<Protected><Flyer /></Protected>} />
            <Route path="/muallems" element={<Protected><Muallems /></Protected>} />
            <Route path="/owners" element={<Protected><Owners /></Protected>} />
            <Route path="/staff" element={<Protected><OfficeStaff /></Protected>} />
            <Route path="/network" element={<Protected><MemberTree /></Protected>} />
            <Route path="/commission" element={<Protected><Commission /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/pilgrim-care" element={<Protected><PilgrimCare /></Protected>} />
            <Route path="/ai-studio" element={<Protected><AiStudio /></Protected>} />
            <Route path="/whatsapp-groups" element={<Protected><WhatsAppGroups /></Protected>} />
            <Route path="/my-designs" element={<Protected><MyDesigns /></Protected>} />
            <Route path="/certificates" element={<Protected><Certificates /></Protected>} />
          </Routes>
          <AiChatWidget />
          <Toaster position="top-right" richColors theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
